/* eslint-disable react/jsx-no-bind */
// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React from 'react';
import block from 'propmods';
import { Button, Icon, RadioButton } from 'lego-on-react';
import DateTime from 'react-datetime';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Monitoring';
import { moment } from '../../lib/moment';
import { calculateRangeType } from '../../utils/date';
import { DateRangeType } from '../../model/time';
import './Datepicker.scss';

const t = i18n(keyset);
const b = block('Datepicker');

interface DateRange {
    title: string;
    getRange: () => { from: moment.Moment; to: moment.Moment };
}

const ranges: Record<DateRangeType, DateRange> = {
    hour: {
        title: t('date-hour'),
        getRange: () => {
            const now = moment();

            return {
                from: now.clone().add('-1', 'hours'),
                to: now,
            };
        },
    },
    day: {
        title: t('date-day'),
        getRange: () => {
            const now = moment();

            return {
                from: now.clone().add('-1', 'days'),
                to: now,
            };
        },
    },
    week: {
        title: t('date-week'),
        getRange: () => {
            const now = moment();

            return {
                from: now.clone().add('-1', 'weeks'),
                to: now,
            };
        },
    },
    month: {
        title: t('date-month'),
        getRange: () => {
            const now = moment();

            return {
                from: now.clone().add('-1', 'months'),
                to: now,
            };
        },
    },
    half_year: {
        title: t('date-half-year'),
        getRange: () => {
            const now = moment();

            return {
                from: now.clone().add('-6', 'months'),
                to: now,
            };
        },
    },
};

interface DatepickerProps {
    to: moment.Moment;
    from: moment.Moment;
    onChange: (_from: moment.Moment, _to: moment.Moment) => void;
    disabled?: boolean;
}

const isLessThanHalfYear = (from: moment.Moment, to: moment.Moment) => {
    const date = from.clone().add(6, 'months');

    return to.isBetween(from, date);
};

export const Datepicker: React.FC<DatepickerProps> = ({ to, from, onChange, disabled }) => {
    const currentRange = calculateRangeType(from, to);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderInput = (props: any) => {
        return (
            <div {...b('datepicker-container')}>
                <Button size="s" theme="normal" onClick={props.onClick} disabled={disabled}>
                    <div {...b('datepicker-label')}>{props.value}</div>
                    <div {...b('caledar-icon')}>
                        <Icon type="calendar" size="s" style={{ width: '28px' }} />
                    </div>
                </Button>
            </div>
        );
    };

    const isValidFrom = (current: moment.Moment) => {
        const v = current.clone().startOf('day');
        return v < to && isLessThanHalfYear(v, to);
    };

    const isValidTo = (current: moment.Moment) => {
        const v = current.clone().endOf('day');
        return v > from && isLessThanHalfYear(from, v);
    };

    return (
        <div {...b()}>
            <div {...b('datepickers')}>
                <DateTime
                    {...{ renderInput }}
                    value={from}
                    closeOnSelect
                    isValidDate={isValidFrom}
                    onChange={(value) => {
                        const newFrom = moment(value);

                        if (!isValidFrom(newFrom)) {
                            return;
                        }

                        onChange(newFrom, to);
                    }}
                />
                <span {...b('datepickers-dash')}>―</span>
                <DateTime
                    {...{ renderInput }}
                    value={to}
                    closeOnSelect
                    isValidDate={isValidTo}
                    onChange={(value) => {
                        const newTo = moment(value);

                        if (!isValidTo(newTo)) {
                            return;
                        }

                        onChange(from, newTo);
                    }}
                />
            </div>
            <div {...b('ranges')}>
                <RadioButton
                    disabled={disabled}
                    size="s"
                    theme="normal"
                    value={currentRange}
                    onChange={(e) => {
                        const key = e.target.value as DateRangeType;

                        if (currentRange === key) {
                            return;
                        }

                        const { getRange } = ranges[key];
                        const { from, to } = getRange();

                        onChange(from, to);
                    }}
                >
                    {Object.entries(ranges).map(([key, { title }]) => {
                        return (
                            <RadioButton.Radio key={key} value={key}>
                                {title}
                            </RadioButton.Radio>
                        );
                    })}
                </RadioButton>
            </div>
        </div>
    );
};
